import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import { authdApiRequest } from '../utils/api';
import LoadingScreen from '../components/LoadingScreen';
import SliderForm from '../components/SliderForm';

function SnapshotCompletionForm() {
  const [loading, setLoading] = useState(true);
  const { snapshotId } = useParams();
  const [snapshot, setSnapshot] = useState(null);
  const [responses, setResponses] = useState({});
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [isFinalPage, setIsFinalPage] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [sliderValues, setSliderValues] = useState([25, 25, 25, 25]);

  useEffect(() => {
    const fetchSnapshot = async () => {
      try {
        const response = await authdApiRequest(`${process.env.REACT_APP_API_HOST}/surveys/${snapshotId}/preformatted`);
        const data = await response.json();
        setSnapshot(data);

        // Check if we need to load the saved state
        if (location.state?.loadSavedState) {
          handleLoadState();
        }
      } catch (error) {
        console.error('Error fetching snapshot:', error);
        toast.error('Failed to load snapshot.');
      } finally {
        setLoading(false);
      }
    };

    fetchSnapshot();
  }, [snapshotId, location.state]);

  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleTopicSelection = (topicId) => {
    setSelectedTopics((prev) => {
      if (prev.includes(topicId)) {
        return prev.filter(id => id !== topicId);
      } else if (prev.length < 5) {
        return [...prev, topicId];
      }
      return prev;
    });
  };

  const handleSliderChange = (newValues) => {
    setSliderValues(newValues);
  };

  const handleSaveState = () => {
    const state = {
      responses,
      selectedTopics,
      sliderValues,
      currentTopicIndex,
    };
    localStorage.setItem(`snapshotState-${snapshotId}`, JSON.stringify(state));
    toast.success('State saved successfully!');
  };

  const handleLoadState = () => {
    const savedState = localStorage.getItem(`snapshotState-${snapshotId}`);
    if (savedState) {
      const { responses, selectedTopics, sliderValues, currentTopicIndex } = JSON.parse(savedState);
      setResponses(responses);
      setSelectedTopics(selectedTopics);
      setSliderValues(sliderValues);
      setCurrentTopicIndex(currentTopicIndex);
      toast.success('State loaded successfully!');
    } else {
      toast.error('No saved state found for this snapshot.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedTopics.length > 5) {
      toast.error('Please select up to 5 topics for improvement.');
      return;
    }
    if (window.confirm('Are you sure you want to submit your responses? You can only submit once.')) {
      try {
        const formattedResponses = Object.entries(responses).map(([questionId, response]) => {
          const question = snapshot.topics
            .flatMap(topic => topic.questions)
            .find(q => q.id === Number(questionId));

          if (!question) {
            console.error(`Question with ID ${questionId} not found.`);
            return null;
          }

          return {
            questionId,
            ...(question.isFreeText ? { freeText: response } : {}),
            ...(question.likertScale ? { likertScore: question.likertScale.indexOf(response) - 2 } : {})
          };
        }).filter(response => response !== null);

        if (formattedResponses.length === 0) {
          toast.error('No valid responses to submit.');
          return;
        }

        await authdApiRequest(`${process.env.REACT_APP_API_HOST}/survey-responses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            snapshotId,
            responses: formattedResponses,
            focusTopics: selectedTopics,
            sliderValues,
          }),
        });
        toast.success('Snapshot completed successfully!');
        navigate('/dashboard');
      } catch (error) {
        console.error('Error submitting responses:', error);
        toast.error('Failed to submit snapshot. Please try again later.');
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const currentTopic = snapshot.topics[currentTopicIndex];

  return (
    <Layout>
      <h1 className="text-3xl font-bold mb-8 text-center">{formatDate(snapshot.endDate)} Snapshot</h1>
      {snapshot.title && <div className="text-center mb-4">{snapshot.title}</div>}
      <form onSubmit={handleSubmit} className="space-y-8 max-w-3xl mx-auto">
        {!isFinalPage ? (
          <>
            <div className="mb-8 p-6 bg-neutral-900 rounded-lg border border-neutral-700">
              <h2 className="text-2xl font-semibold mb-4 text-sky-300">{currentTopic.name}</h2>
              {currentTopic.questions.map((question, i) => (
                <div key={question.id} className={`${i !== 0 ? 'mt-8' : ''}`}>
                  <h3 className="mb-2 font-bold">{question.question}</h3>
                  {question.isFreeText ? (
                    <textarea
                      className="w-full p-3 border border-neutral-700 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                      rows="4"
                      value={responses[question.id] || ''}
                      onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    />
                  ) : (
                    <div className="flex flex-col space-y-2">
                      {question.likertScale.map((option, index) => {
                        const isSelected = responses[question.id] === option;
                        return (
                          <label key={index} className={`flex items-center ${isSelected ? 'text-sky-500 font-bold' : ''}`}>
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              value={option}
                              checked={isSelected}
                              onChange={() => handleResponseChange(question.id, option)}
                              className={`mr-2 focus:ring-sky-500 fancy-radio ${isSelected ? 'fancy-radio' : ''}`}
                            />
                            <span className="text-sm">{option}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between">
              {currentTopicIndex > 0 ? (
                <div className="basis-1/3">
                  <button
                    type="button"
                    onClick={() => setCurrentTopicIndex((prev) => Math.max(prev - 1, 0))}
                    className="py-2 px-6 bg-sky-500 text-white rounded-md select-none"
                  >
                    &lt;
                  </button>
                </div>
              ) : (
                <div className="basis-1/3"></div>
              )}
              <div className="basis-1/3 text-center">
                <button
                  type="button"
                  onClick={handleSaveState}
                  className="py-2 px-4 bg-neutral-500 text-white rounded-md select-none"
                >
                  Save
                </button>
              </div>
              <div className="basis-1/3 text-right">
                <button
                  type="button"
                  onClick={() => {
                    if (currentTopicIndex === snapshot.topics.length - 1) {
                      setIsFinalPage(true);
                    } else {
                      setCurrentTopicIndex((prev) => Math.min(prev + 1, snapshot.topics.length - 1));
                    }
                    //window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="py-2 px-6 bg-sky-500 text-white rounded-md select-none"
                >
                  &gt;
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mb-8 p-6 bg-neutral-900 rounded-lg border border-neutral-700">
              <h2 className="text-2xl font-semibold mb-2 text-sky-300">Which topics could use the most improvement?</h2>
              <div className="text-sm text-neutral-300 mb-4">Select up to 5 topics</div>
              {snapshot.topics.map((topic) => (
                <label key={topic.id} className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={selectedTopics.includes(topic.id)}
                    onChange={() => handleTopicSelection(topic.id)}
                    className="mr-2 peer"
                    disabled={!selectedTopics.includes(topic.id) && selectedTopics.length >= 5}
                  />
                  <span className="text-sm peer-checked:text-sky-500 peer-checked:font-bold">{topic.name}</span>
                </label>
              ))}
            </div>
            <div className="mb-8 p-6 bg-neutral-900 rounded-lg border border-neutral-700">
              <h2 className="text-2xl font-semibold mb-2 text-sky-300">During the average sprint, what percentage of your time is spent between the following areas?</h2>
              <SliderForm values={sliderValues} onChange={handleSliderChange} />
            </div>
            <div className="flex justify-between">
              <div className="basis-1/3">
                <button
                  type="button"
                  onClick={() => setIsFinalPage(false)}
                  className="py-2 px-6 bg-sky-500 text-white rounded-md select-none"
                >
                  &lt;
                </button>
              </div>
              <div className="basis-1/3 text-center">
                <button
                  type="button"
                  onClick={handleSaveState}
                  className="py-2 px-4 bg-neutral-500 text-white rounded-md select-none"
                >
                  Save
                </button>
              </div>
              <div className="basis-1/3"></div>
            </div>
            <div className="text-center">
                <button
                  type="submit"
                  className="py-4 px-8 text-lg bg-green-500 text-white rounded-md select-none"
                >
                  Submit Snapshot
                </button>
            </div>
          </>
        )}
      </form>
    </Layout>
  );
}

export default SnapshotCompletionForm;
